import React from "react";
import classNames from "classnames";
import {
    AccountsIcon,
    InvoicingIcon,
    TasksIcon,
    TransactionsIcon,
} from "@icons";
import { NavigationItem } from "@design-system/Navigation/NavigationItem";
import { NavigationMenu } from "@design-system/Navigation/NavigationMenu";
import navigationStyles from "@design-system/Navigation/NavigationMenu.module.scss";
import styles from "./AppShellPlayground.module.scss";

const navigationRoutes = ["/tasks", "/accounts", "/transactions"] as const;

export type AppShellPlaygroundRoute = (typeof navigationRoutes)[number];

const navigationEntries: ReadonlyArray<{
    route: AppShellPlaygroundRoute;
    label: string;
    icon: React.ReactNode;
}> = [
    { route: "/tasks", label: "Tasks", icon: <TasksIcon /> },
    { route: "/accounts", label: "Accounts", icon: <AccountsIcon /> },
    {
        route: "/transactions",
        label: "Transactions",
        icon: <TransactionsIcon />,
    },
];

/**
 * Demo navigation for the AppShell playground: real navigation primitives and
 * icons so the reserved navigation width, active state, and disabled state
 * match production. Selecting an item switches the shell's layout mode, which
 * is how the sizing modes map to routes in the app.
 */
export const AppShellPlaygroundNavigation = ({
    isOpen,
    activeRoute,
    onSelectRoute,
}: {
    isOpen: boolean;
    activeRoute: AppShellPlaygroundRoute;
    onSelectRoute: (route: AppShellPlaygroundRoute) => void;
}) => (
    <div
        className={classNames(styles.demoNavigationSlot, {
            [styles.demoNavigationSlotOpen]: isOpen,
        })}
    >
        <div
            className={classNames(styles.demoNavigation, {
                [styles.demoNavigationClosed]: !isOpen,
            })}
        >
            <div className={styles.demoNavigationHeader}>
                <span>Demo workspace</span>
                <span className={styles.slotLabel}>navigation</span>
            </div>
            <NavigationMenu>
                {navigationEntries.map(({ route, label, icon }) => (
                    <button
                        key={route}
                        type="button"
                        onClick={() => onSelectRoute(route)}
                        className={classNames(
                            navigationStyles.navigationAction,
                            styles.demoNavigationAction,
                            { active: route === activeRoute },
                        )}
                    >
                        <NavigationItem
                            icon={icon}
                            activeIndicator={route === activeRoute}
                        >
                            {label}
                        </NavigationItem>
                    </button>
                ))}
                <button
                    type="button"
                    disabled
                    className={classNames(
                        navigationStyles.navigationAction,
                        styles.demoNavigationAction,
                    )}
                >
                    <NavigationItem icon={<InvoicingIcon />} disabled>
                        Invoicing
                    </NavigationItem>
                </button>
            </NavigationMenu>
        </div>
    </div>
);
